/*
  voltage: free gift automation for the tiered cart.

  Defines two custom elements used by voltage--free-shipping-bar--tiered-free-gifts.liquid:
    <free-gift-adds>     - auto-adds qualifying gifts when a cart threshold is met
    <free-gift-removals> - removes gifts when the cart drops below their threshold

  Extracted from assets/theme.js on 2026-07-15. It previously lived inside that vendor
  file, where a Stretch update would have silently dropped it and broken free gifts with
  no error. Nothing here imports from "theme" or "vendor" - it is self-contained - so it
  loads as a standalone module from layout/theme.liquid.

  Do not move this back into theme.js.
*/

if (!window.customElements.get('free-gift-removals')) {

  class FreeGiftRemovals extends HTMLElement {

    constructor() {
      super();
      this.processed = false;
    }

    connectedCallback() {
      this.cartDrawer = document.querySelector('cart-drawer');
      this.init();
    }

    init() {
      if (this.removals && !this.processed) {
        this.processed = true;
        this.removeGifts();
      }
    }

    async removeGifts() {

      try {

        // voltage: serialize across drawer re-renders — every re-render builds a
        // fresh instance with processed=false, and overlapping removals were one
        // half of the 2026-07 removal hang
        if (window.voltageFreeGiftRemovalBusy) return;
        window.voltageFreeGiftRemovalBusy = true;

        const updates = {};

        for (let i = 0; i < this.removals.length; i++) {
          const removal = this.removals[i];
          updates[removal.dataset.key] = 0;
        }

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

        this.fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            updates: updates,
            sections: sectionsToBundle.join(",")
          })
        };

        const response = await fetch(`${Shopify.routes.root}cart/update.js`, this.fetchRequestOpts);

        // voltage: the other half of the hang — a failed update (stale line keys
        // return 422) still dispatched cart:change, the drawer re-rendered, the
        // server still saw gifts to remove, and a fresh instance fired again,
        // forever. On failure: stop silently; the next genuine cart change retries.
        if (!response.ok) {
          window.voltageFreeGiftRemovalBusy = false;
          return;
        }

        if (this.cartDrawer) {

          const cartContent = await response.json();

          document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              cart: cartContent
            }
          }));

          window.voltageFreeGiftRemovalBusy = false;

        }
        else {
          location.reload();
        }

      } catch (error) {

        window.voltageFreeGiftRemovalBusy = false;

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: error.message
          }
        }));

      }

    }

    get removals() {
      return this.querySelectorAll('free-gift-remove');
    }

  }

  window.customElements.define("free-gift-removals", FreeGiftRemovals);

}


if (!window.customElements.get('free-gift-adds')) {

  class FreeGiftAdds extends HTMLElement {

    constructor() {
      super();
      this.processed = false;
    }

    connectedCallback() {
      this.cartDrawer = document.querySelector('cart-drawer');
      this.init();
    }

    init() {
      if (this.adds && !this.processed) {
        this.processed = true;
        this.addGifts();
      }
    }

    async addGifts() {

      try {

        let postData = {
          'items': []
        };

        for (let i = 0; i < this.adds.length; i++) {

          const add = this.adds[i];
          const quantity = 1;

          let item = {};

          item.id = Number(add.dataset.variantId);
          item.quantity = quantity;

          item.properties = {};
          item.properties["Free Gift"] = add.dataset.giftTitle;
          item.properties["_Free Gift Number"] = add.dataset.giftIndex;
          item.properties["_Free Gift ID"] = add.dataset.giftId;

          postData.items.push(item)

        }

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));

        postData.sections = sectionsToBundle.join(",");

        let fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(postData)
        };

        const response = await fetch(`${Shopify.routes.root}cart/add.js`, fetchRequestOpts);

        if (response.ok) {

          const cartContent = await response.json();

          if (this.cartDrawer) {

            document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
              bubbles: true,
              detail: {
                baseEvent: "variant:add",
                cart: cartContent
              }
            }));

          }
          else {
            location.reload();
          }

        }
        else {

          const responseJson = await response.json();

          document.dispatchEvent(new CustomEvent("toast:show", {
            detail: {
              message: responseJson["message"],
              tone: "error"
            }
          }));

        }

      } catch (error) {

        console.log(error);

        /*
        document.dispatchEvent(new CustomEvent("toast:show", {
          bubbles: true,
          detail: {
            message: error.message,
            tone: "error"
          }
        }));
        */

      }

    }

    get adds() {
      return this.querySelectorAll('free-gift-add');
    }

  }

  window.customElements.define("free-gift-adds", FreeGiftAdds);

}


/*
  voltage: bundle gift-with-purchase cart sync (Phase 2, 2026-08-24).

  Keeps the cart's gift lines converged with its bundle lines:
    - a bundle line = any line carrying properties._vgo_gift (comma list of gift
      variant ids, injected into the product form by gift-with-purchase-voltage
      only while a selling plan is selected) AND a selling_plan_allocation.
      Plan required at add time = first order only: the gift line itself is
      added WITHOUT a selling plan, so Awtomic's subscription contract never
      contains it and recurring orders exclude it natively.
    - a gift line = any line carrying properties._vgo_bundle_gift (set here).
      It also gets a visible "Free Gift" property so snippets/line-item.liquid's
      existing treatment (badge, locked qty) applies unchanged.

  Loop-proof by design: sync() diffs needed-vs-present and dispatches cart:change
  ONLY when it actually changed the cart, so the re-render it causes runs one
  more sync that finds nothing to do and stops. This is the discipline the
  original tiered remover lacked (see the hang fix above).

  The $0 pricing is NOT done here — SUKU runs an automatic Buy X Get Y discount
  (bundle product -> gift product 100% off), which also draws the strikethrough
  and savings line natively. Known accepted hole: automatic discounts can't
  require a selling plan, so the unlisted gift product is free for anyone who
  has its URL; managed operationally.

  A tiered threshold gift and a bundle gift must never be the SAME product:
  below threshold, the tiered remover matches gift lines by variant id and
  would delete the bundle's gift. Different products = no interaction.
*/
if (!window.voltageBundleGiftSync) {

  window.voltageBundleGiftSync = true;

  const GIFT_LIST_PROP = '_vgo_gift';
  const GIFT_LINE_PROP = '_vgo_bundle_gift';
  let busy = false;
  let rerun = false;
  let timer = null;

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(sync, 100);
  };

  // variant:add / cart:change / line-item:change carry the full cart in
  // detail.cart (documented) — diff on it directly, skipping the debounce and
  // the cart.js round-trip that made the gift pop in ~400ms late. Events
  // without a payload (cart:refresh, initial load) fall back to the fetch.
  const onCartEvent = (event) => {
    const cart = event && event.detail && event.detail.cart;
    if (cart && Array.isArray(cart.items)) {
      clearTimeout(timer);
      sync(cart);
    } else {
      schedule();
    }
  };

  async function sync(prefetchedCart) {

    if (busy) { rerun = true; return; }
    busy = true;

    try {

      let cart = prefetchedCart;
      if (!cart) {
        const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) { busy = false; return; }
        cart = await cartResponse.json();
      }

      // gift variant id -> owning bundle variant id (only while that line subscribes)
      const needed = new Map();
      for (const item of cart.items) {
        const ids = item.properties && item.properties[GIFT_LIST_PROP];
        if (ids && item.selling_plan_allocation) {
          String(ids).split(',').forEach((id) => {
            id = id.trim();
            if (id) needed.set(id, String(item.variant_id));
          });
        }
      }

      // gift variant id -> its line key
      const present = new Map();
      for (const item of cart.items) {
        if (item.properties && item.properties[GIFT_LINE_PROP] != null) {
          present.set(String(item.variant_id), item.key);
        }
      }

      const toAdd = [...needed.keys()].filter((id) => !present.has(id));
      const toRemove = [...present.keys()].filter((id) => !needed.has(id));

      // converged — no writes, no events, no loop
      if (!toAdd.length && !toRemove.length) { busy = false; return; }

      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

      let lastResponseJson = null;

      if (toRemove.length) {
        const updates = {};
        toRemove.forEach((id) => { updates[present.get(id)] = 0; });
        const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates, sections: toAdd.length ? '' : sectionsToBundle.join(',') })
        });
        if (!response.ok) { busy = false; return; }
        lastResponseJson = await response.json();
      }

      if (toAdd.length) {
        const items = toAdd.map((id) => ({
          id: Number(id),
          quantity: 1,
          properties: {
            'Free Gift': 'Bundle gift with purchase',
            [GIFT_LINE_PROP]: needed.get(id)
          }
        }));
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items, sections: sectionsToBundle.join(',') })
        });
        if (!response.ok) { busy = false; return; }
        lastResponseJson = await response.json();
      }

      busy = false;

      // Maestrooo's documented mechanism for custom code: cart:refresh on the
      // documentElement makes the theme re-fetch and re-render the cart UI
      // itself. Hand-assembling a cart:change payload is what left the drawer
      // stale until reload (cart/add.js returns items, not the full cart).
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      if (rerun) { rerun = false; schedule(); }

    } catch (error) {
      busy = false;
      console.log('voltage bundle gift sync:', error);
    }

  }

  ['cart:change', 'cart:refresh', 'variant:add', 'line-item:change'].forEach((name) => {
    document.addEventListener(name, onCartEvent);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }

}


/*
  voltage: keep each bundle gift line directly BELOW its owning bundle line
  (client feedback 2026-08-24 via SUKU review). Shopify prepends the newest
  cart line, and the gift is added right after the bundle, so it naturally
  lands ABOVE it. line-item.liquid stamps data-vgo-variant-id on every line
  and data-vgo-gift-owner (the owning bundle's variant id) on gift lines;
  this observer moves the gift's row (its <tr> on the cart page, the
  <line-item> itself in the drawer) to sit right after the owner. Convergent:
  a correct order produces zero moves, so our own mutations settle immediately.
  CSS in voltage.css draws the indent + connector elbow off the same attribute.
*/
if (!window.voltageGiftLineOrder) {

  window.voltageGiftLineOrder = true;

  const unitOf = (el) => el.closest('tr') || el;

  function reorder() {
    document.querySelectorAll('line-item[data-vgo-gift-owner]').forEach((gift) => {
      const ownerId = gift.dataset.vgoGiftOwner;
      const giftUnit = unitOf(gift);
      const owner = Array.from(giftUnit.parentNode ? giftUnit.parentNode.parentNode.querySelectorAll(`line-item[data-vgo-variant-id="${ownerId}"]:not([data-vgo-gift-owner])`) : [])
        .map(unitOf)
        .find((u) => u.parentNode === giftUnit.parentNode);
      if (owner && owner.nextElementSibling !== giftUnit) owner.after(giftUnit);
    });
  }

  let raf = null;
  const observer = new MutationObserver(() => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = null; reorder(); });
  });
  observer.observe(document.body, { childList: true, subtree: true });
  reorder();

}


/*
  voltage: promo-only products (seo.hidden = 1) can't be bought on their own
  (SUKU 2026-09-28 — the bundle gift is a smaller, cheaper SKU people were
  finding by URL and buying). The product page redirects home
  (voltage-main-product.liquid); this catches the cart side. line-item.liquid
  stamps data-vgo-hidden-line="<line key>" on any such line that is NOT a
  bundle gift (_vgo_bundle_gift) or a tiered threshold gift (_Free Gift ID);
  we zero those lines and let the theme re-render. Each key is tried once so
  a failed request can't loop. Checkout permalinks (/cart/ID:1) skip the cart
  entirely — only a checkout validation Function closes that.
*/
if (!window.voltageHiddenLineGuard) {

  window.voltageHiddenLineGuard = true;

  const tried = new Set();
  let running = false;

  async function removeHiddenLines() {
    if (running) return;
    const keys = [...new Set(Array.from(document.querySelectorAll('line-item[data-vgo-hidden-line]'))
      .map((el) => el.dataset.vgoHiddenLine))].filter((key) => key && !tried.has(key));
    if (!keys.length) return;

    running = true;
    keys.forEach((key) => tried.add(key));
    try {
      const updates = {};
      keys.forEach((key) => { updates[key] = 0; });
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      });
      if (response.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    } catch (error) {
      console.log('voltage hidden line guard:', error);
    }
    running = false;
  }

  let raf = null;
  new MutationObserver(() => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = null; removeHiddenLines(); });
  }).observe(document.body, { childList: true, subtree: true });
  removeHiddenLines();

}
